import { useLanguage } from '@/i18n/LanguageContext';
import { rangeInvalid, type ListFilterState, type ListSortValue } from '@/lib/listFilters';

type Option = { readonly value: string; readonly label: string };

/**
 * The filter bar of the inbound / outbound lists: status, client (only when the seller has several), warehouse (only when
 * there are several), a free date range (from / to, both days included), sort by date, and Reset. Everything is optional;
 * the empty bar means "all records". Each control is a labelled native input (keyboard and screen-reader friendly).
 */
export function ListFilterBar({
  filters, onChange, onReset, filtered, statuses, owners, warehouses,
}: {
  readonly filters: ListFilterState;
  readonly onChange: (patch: Partial<ListFilterState>) => void;
  readonly onReset: () => void;
  readonly filtered: boolean;
  readonly statuses: readonly Option[];
  readonly owners: readonly Option[];
  readonly warehouses: readonly Option[];
}): JSX.Element {
  const { t } = useLanguage();
  const invalid = rangeInvalid(filters);
  return (
    <div className="filters" role="search" aria-label={t('filters.aria')}>
      <label>
        {t('common.status')}
        <select value={filters.status} onChange={(e) => onChange({ status: e.target.value })}>
          <option value="">{t('filters.all')}</option>
          {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </label>
      {owners.length > 1 && (
        <label>
          {t('inbounds.colSeller')}
          <select value={filters.ownerId} onChange={(e) => onChange({ ownerId: e.target.value })}>
            <option value="">{t('filters.allSellers')}</option>
            {owners.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      )}
      {warehouses.length > 1 && (
        <label>
          {t('inbounds.colWarehouse')}
          <select value={filters.warehouseId} onChange={(e) => onChange({ warehouseId: e.target.value })}>
            <option value="">{t('filters.allWarehouses')}</option>
            {warehouses.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        </label>
      )}
      <label>
        {t('filters.from')}
        <input type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => onChange({ from: e.target.value })} />
      </label>
      <label>
        {t('filters.to')}
        <input type="date" value={filters.to} min={filters.from || undefined} aria-invalid={invalid} onChange={(e) => onChange({ to: e.target.value })} />
      </label>
      <label>
        {t('filters.sort')}
        <select value={filters.sort} onChange={(e) => onChange({ sort: e.target.value as ListSortValue })}>
          <option value="created_desc">{t('filters.newest')}</option>
          <option value="created_asc">{t('filters.oldest')}</option>
        </select>
      </label>
      <button type="button" onClick={onReset} disabled={!filtered}>{t('filters.reset')}</button>
      {invalid && <span className="hint" role="alert">{t('filters.rangeInvalid')}</span>}
    </div>
  );
}
